<i18n>
{
	"en": {
		"fromAddress": "From Address",
		"toAddress": "To Address",
		"missingDriver": "Missing driver",
		"duration": "Duration",
		"days": "Day | Days",
		"departure": "Departure time",
		"arrival": "Arrival time",
		"selectTime": "Select time",
		"invalidDateObject": "Time is a mandatory field",
		"timeNotSet": "Time is a mandatory field",
		"date1IsNotBeforeDate2": "Planned departure is before planned arrival."
	},
	"no": {
		"fromAddress": "Fra adresse",
		"toAddress": "Til adresse",
		"missingDriver": "Mangler sjåfør",
		"duration": "Varighet",
		"days": "Dag | Dager",
		"departure": "Avgangstid",
		"arrival": "Ankomsttid",
		"selectTime": "Velg tid",
		"invalidDateObject": "Tidspunkt er påkrevd",
		"timeNotSet": "Tidspunkt er påkrevd",
		"date1IsNotBeforeDate2": "Planlagt avgang er før planlagt ankomst."
	}
}
</i18n>

<template>
	<t-card class="leg" border="default">
		<div slot="header" class="leg-header">
			<div class="leg-header-left">
				<span class="leg-header-title">Leg {{legNumber + 1}}</span>
				<div class="leg-header-arrow-buttons">
					<t-icon-button
						v-if="legNumber > 0"
						type="bp"
						icon="arrow-up"
						appearance="minimal"
						@click="moveLegUp"
					/>
					<t-icon-button
						v-if="legNumber < numOfLegs - 1"
						type="bp"
						icon="arrow-down"
						appearance="minimal"
						@click="moveLegDown"
					/>
				</div>
			</div>
			<t-icon-button
				:disabled="removeDisabled"
				appearance="minimal"
				intent="danger"
				icon="cross"
				type="bp"
				size="small"
				iconSize="xs"
				@click="removeLeg(legNumber)"
			/>
		</div>

		<div class="leg-content">
			<div class="grid-left">
				<TripAddressInput
					:title="$t('fromAddress')"
					:value="leg.fromAddress"
					:isInvalid="Boolean(errors.fromAddress)"
					:validationKey="errors.fromAddress"
					focusNextOnSelected
					@change="setLegFromAddress({ legNumber, ...$event})"
				/>

				<t-form-group :title="$t('departure')">
					<t-time-picker
						:value="leg.departureTime"
						:placeholder="$t('selectTime')"
						:isInvalid="Boolean(errors.etd)"
						:validationMessage="$t(errors.etd)"
						@input="setLegDepartureTime({ legNumber, departureTime: $event }) && focusNext()"
					/>
				</t-form-group>

				<div style="display: grid; grid-template-columns: 80px 1fr; column-gap: 20px;">
					<t-form-group
						:title="$t('duration')"
					>
						<t-number-input
							:value="leg.duration"
							:extraDisplayText="$tc('days', leg.duration)"
							castToNumber
							leftAlign
							@focus="$event.target.select()"
							@change="setLegDuration({ legNumber, duration: $event }) && focusNext()"
						/>
					</t-form-group>

					<OperationalStatusFormInput
						:selectedItem="leg.status"
						:availableStatuses="selectableStatuses"
						showPopoverOnFocus
						@change="setLegStatus({legNumber, status: $event}) && focusNext()"
					/>
				</div>

				<TripAddressInput
					:title="$t('toAddress')"
					:value="leg.toAddress"
					:isInvalid="Boolean(errors.toAddress)"
					:validationKey="errors.toAddress"
					focusNextOnSelected
					@change="setLegToAddress({ legNumber, ...$event})"
				/>

				<t-form-group :title="$t('arrival')">
					<t-time-picker
						:value="leg.arrivalTime"
						:placeholder="$t('selectTime')"
						:isInvalid="Boolean(errors.eta)"
						:validationMessage="$t(errors.eta)"
						@input="setLegArrivalTime({ legNumber, arrivalTime: $event }) && focusNext()"
					/>
				</t-form-group>

				<DriverInput
					ref="DriversFormInput"
					style="min-height: 112px;"
					:value="leg.driver"
					:needAccept="leg.needAccept"
					:isInvalid="Boolean(errors.driver)"
					:validationMessage="$t(errors.driver)"
					@change:needAccept="setLegNeedAccept({ legNumber, needAccept: $event })"
					@change="setLegDriver({ legNumber, ...$event })
						&& Object.keys($event).length
						&& focusNext({ target: getFirstInputElementByRef($refs.DriversFormInput) })"
				/>
			</div>

			<div class="grid-right">
				<CargoCarriersInput
					ref="cargoCarriersInput"
					isInputForLeg
					:value="leg.cargoCarrier"
					@change="setLegCargoCarrier({ legNumber, ...$event })
						&& Object.keys($event).length
						&& focusNext({ target: getFirstInputElementByRef($refs.cargoCarriersInput) })"
				/>
				<VendorInput
					ref="VendorsFormInput"
					:value="leg.vendor"
					:isInvalid="false"
					:validationKey="errors.vendor"
					@change="setLegVendor({ legNumber, ...$event })
						&& Object.keys($event).length
						&& focusNext({ target: getFirstInputElementByRef($refs.VendorsFormInput) })"
				/>
			</div>

		</div>
	</t-card>
</template>

<script>
import CargoCarriersInput from '@/modules/cargoCarriers/CargoCarriersInput.vue';
import VendorInput from '@/modules/vendors/VendorInput.vue';
import DriverInput from '@/modules/drivers/DriverInput.vue';
import OperationalStatusFormInput from '@/modules/operationalStatus/OperationalStatusFormInput.vue';
import TripAddressInput from '@/modules/tripAddresses/TripAddressInput';

import { mapState, mapActions } from '../../store/tripTemplates.store.js';
import {
	statuses,
} from '@/core';

export default {
	name: 'Leg',
	components: {
		CargoCarriersInput,
		VendorInput,
		DriverInput,
		OperationalStatusFormInput,
		TripAddressInput,
	},

	props: { leg: Object, numOfLegs: Number },
	data() {
		return {
			showAddressSearch: false,
			selectableStatuses: Object.values(statuses.trip),
			showSaveBeforeSendDialog: false,
			broadcastDriversIsShow: false,
		};
	},

	computed: {
		...mapState({
			templateDetails: 'templateDetails',
			dirty: 'dirty',
			errors(state) {
				return state.errors.legs && state.errors.legs[this.legNumber]
					? state.errors.legs[this.legNumber]
					: {};
			},
		}),
		legNumber() {
			return this.leg.legNumber;
		},
		removeDisabled() {
			return this.templateDetails.legs.length < 2;
		},
	},

	methods: {
		...mapActions([
			'removeLeg',
			'swapLegOrder',
			'setLegStatus',
			'setLegFromAddress',
			'setLegToAddress',
			'setLegDepartureTime',
			'setLegArrivalTime',
			'setLegDuration',
			'setLegCargoCarrier',
			'setLegVendor',
			'setLegNeedAccept',
			'setLegDriver',
		]),
		moveLegUp() {
			this.swapLegOrder({ legNumberBefore: this.legNumber, legNumberAfter: this.legNumber - 1 });
		},
		moveLegDown() {
			this.swapLegOrder({ legNumberBefore: this.legNumber, legNumberAfter: this.legNumber + 1 });
		},

		getFirstInputElementByRef(ref) {
			return ref.$el.querySelector('input');
		},
	},
};
</script>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/colors';
@import '~@/components/evergreen/scss/text';

.leg {
	margin-bottom: 10px;
}

.leg-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	background: get-color($background, 'tint1');
	border-bottom: 1px solid #EDF0F2;
	padding: 2px 2px 2px 8px;
}

.leg-header-title {
	color: get-color($text, 'default');
	font-weight: 600;
	font-size: 12px;
	padding: 3px 0px;
}

.leg-header-left {
	display: grid;
	align-items: center;
	column-gap: 10px;
	grid-auto-flow: column;
	grid-template-columns: auto-fill;
}

.leg-header-arrow-buttons {
	display: flex;
}

.leg-content {
	display: grid;
	row-gap: 20px;
	grid-template-columns: 700px 1fr;
	grid-template-areas:
		"left right";
	padding: 7px;
}

.grid-left {
	display: grid;
	gap: 20px;
	grid-template-columns: 	310px
							110px
							1fr;
	grid-template-rows: auto auto 1fr;
	grid-area: left;
}

.grid-right {
	display: grid;
	gap: 20px;
	grid-area: right;
	grid-template-rows: repeat(2, minmax(min-content, max-content));

	border-left: 1px solid get-color($border, 'default');
	margin-left: 20px;
	padding-left: 19px;
}
</style>
